import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, ChapterHeader } from "./Reveal";
import { MapPin, Phone, Mail, User, Send, Loader2 } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_SRC = "https://maps.google.com/maps?q=Are%C3%A1l%20ZTS%20Dubnica%20nad%20V%C3%A1hom&z=14&output=embed";

const inputCls = "w-full bg-ink border border-steelborder text-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-safety transition-colors duration-300 placeholder:text-slate-600";

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, message: e.detail }));
    window.addEventListener("prefill-enquiry", onPrefill);
    return () => window.removeEventListener("prefill-enquiry", onPrefill);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null });
      toast.success(t("toast_ok"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t("toast_err"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="bg-gunmetal border-t border-steelborder">
      <div className="py-20 lg:py-28 px-4 sm:px-8 max-w-7xl mx-auto">
        <ChapterHeader number="05" eyebrow={t("c5_eyebrow")} title={t("c5_title")} />

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-2 space-y-8">
            <Reveal>
              <div data-testid="contact-company-card" className="border border-steelborder bg-steelcard p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-safety" />
                  <h3 className="font-heading text-xl font-bold uppercase text-slate-100">{t("co_name")}</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {t("co_addr1")}<br />{t("co_addr2")}<br />{t("co_addr3")}
                </p>
                <div className="mt-4 pt-4 border-t border-steelborder space-y-2 font-mono text-xs text-slate-400">
                  <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-safety" /> {t("tel")} <a href="tel:+421905207101" data-testid="contact-phone-1" className="hover:text-safety transition-colors">0905 207 101</a></p>
                  <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-safety" /> <a href="mailto:ztsfinvest@ztsfinvest.sk" data-testid="contact-email-link" className="hover:text-safety transition-colors">ztsfinvest@ztsfinvest.sk</a></p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div data-testid="contact-director-card" className="border border-steelborder bg-steelcard p-6">
                <div className="flex items-center gap-3 mb-4">
                  <User className="w-5 h-5 text-safety" />
                  <h3 className="font-heading text-xl font-bold uppercase text-slate-100">{t("director")}</h3>
                </div>
                <p className="text-base text-slate-200 font-semibold">{t("director_name")}</p>
                <p className="text-sm text-slate-500">{t("director_role")}</p>
                <div className="mt-4 pt-4 border-t border-steelborder space-y-2 font-mono text-xs text-slate-400">
                  <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-safety" /> {t("mobil")} <a href="tel:+421905207101" data-testid="director-phone-link" className="hover:text-safety transition-colors">0905 207 101</a></p>
                  <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-safety" /> <a href="mailto:holecek@ztsfinvest.sk" data-testid="director-email-link" className="hover:text-safety transition-colors">holecek@ztsfinvest.sk</a></p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div data-testid="company-ids" className="border border-steelborder bg-ink p-6 font-mono text-xs text-slate-500 space-y-1.5">
                <p>IČO: <span className="text-slate-300">36319015</span></p>
                <p>DIČ: <span className="text-slate-300">2020116109</span></p>
                <p>IČ DPH: <span className="text-slate-300">SK 2020116109</span></p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="border border-steelborder bg-steelcard p-6 sm:p-8 lg:p-10 space-y-5">
              <p className="font-heading text-2xl font-bold uppercase text-slate-100">{t("form_title")}</p>
              <p className="text-sm text-slate-500 -mt-2">{t("form_sub")}</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="cf-name" className="block font-mono text-xs tracking-[0.2em] uppercase text-slate-500 mb-2">{t("f_name")}</label>
                  <input id="cf-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder={t("ph_name")} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block font-mono text-xs tracking-[0.2em] uppercase text-slate-500 mb-2">{t("f_email")}</label>
                  <input id="cf-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className={inputCls} placeholder={t("ph_email")} />
                </div>
              </div>
              <div>
                <label htmlFor="cf-phone" className="block font-mono text-xs tracking-[0.2em] uppercase text-slate-500 mb-2">{t("f_phone")}</label>
                <input id="cf-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={t("ph_phone")} />
              </div>
              <div>
                <label htmlFor="cf-message" className="block font-mono text-xs tracking-[0.2em] uppercase text-slate-500 mb-2">{t("f_message")}</label>
                <textarea id="cf-message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder={t("ph_msg")} />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit-button"
                className="group flex items-center justify-center gap-3 w-full bg-safety text-ink font-heading font-bold uppercase tracking-wide text-lg px-7 py-4 hover:bg-safety-hover disabled:opacity-60 transition-colors duration-300">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? t("sending") : t("submit")}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-14">
          <div data-testid="map-wrapper" className="relative border border-steelborder">
            <iframe title="Mapa – Areál ZTS, Dubnica nad Váhom" data-testid="map-iframe"
              src={MAP_SRC} className="w-full h-[380px] grayscale invert-[0.9] contrast-[0.9]"
              loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <div className="absolute top-4 left-4 font-mono text-xs tracking-[0.2em] uppercase text-slate-200 bg-ink/80 backdrop-blur px-3 py-1.5 border border-steelborder pointer-events-none">
              {t("map_label")}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
